import * as THREE from 'three';
import { Action } from './Actions';
import { EventDispatcher } from './EventDispatcher';
import { Utils } from './utils';
import { resourcePath } from './potreeState';

/** jQuery-like object interface for DOM manipulation used by Annotation */
interface JQueryLike {
  find(selector: string): JQueryLike;
  append(content: string | JQueryLike): JQueryLike;
  click(handler: (e?: unknown) => void): JQueryLike;
  css(prop: string, value: string): JQueryLike;
  empty(): JQueryLike;
  show(): JQueryLike;
  hide(): JQueryLike;
  fadeIn(duration: number): JQueryLike;
  hover(handlerIn: (e: unknown) => void, handlerOut: (e: unknown) => void): JQueryLike;
  mouseenter(handler: (e: unknown) => void): JQueryLike;
  mouseleave(handler: (e: unknown) => void): JQueryLike;
  on(event: string, handler: (e: unknown) => void): JQueryLike;
  draggable(options: Record<string, unknown>): JQueryLike;
  remove(): void;
  parentElement: HTMLElement | null;
  [index: number]: HTMLElement;
}

/** Declare the global jQuery $ function */
declare const $: (selector: string | HTMLElement) => JQueryLike;

/** TWEEN library global declaration */
interface TweenInstance {
  to(target: unknown, duration: number): TweenInstance;
  easing(fn: unknown): TweenInstance;
  onUpdate(fn: () => void): TweenInstance;
  onComplete(fn: () => void): TweenInstance;
  start(): TweenInstance;
  stop(): void;
}

declare const TWEEN: {
  Easing: {
    Quartic: { Out: unknown };
  };
  Tween: new (target: unknown) => TweenInstance;
};

/** Scene interface subset used by Annotation */
interface AnnotationScene {
  getActiveCamera(): THREE.Camera;
  view: {
    position: THREE.Vector3;
    direction: THREE.Vector3;
    radius: number;
    lookAt(target: THREE.Vector3): void;
    /** Track a camera-motion tween for cancellation on view switches (see viewer/View.ts). */
    registerMotionTween(tween: TweenInstance): TweenInstance;
  };
}

/** Viewer interface subset used by Annotation */
interface AnnotationViewer {
  renderArea: HTMLElement;
  renderer: THREE.WebGLRenderer;
  addEventListener(type: string, handler: () => void): void;
  removeEventListener(type: string, handler: () => void): void;
  scene: AnnotationScene;
}

/** Constructor args for Annotation */
interface AnnotationArgs {
  title?: string;
  description?: string;
  position?: THREE.Vector3 | number[] | { x: number | null; y?: number; z?: number };
  cameraPosition?: THREE.Vector3 | number[];
  cameraTarget?: THREE.Vector3 | number[];
  radius?: number;
  view?: unknown;
  actions?: Array<Action | Record<string, unknown>>;
  collapseThreshold?: number;
}

interface AnnotationHandles {
  domElement: JQueryLike;
  setCoordinates: (start: THREE.Vector3, end: THREE.Vector3) => void;
  updateCallback: () => void;
}

export class Annotation extends EventDispatcher {
  scene: AnnotationScene | null;
  _title: string;
  _description: string;
  offset: THREE.Vector3;
  uuid: string;
  position: THREE.Vector3 | null;
  cameraPosition: THREE.Vector3 | undefined;
  cameraTarget: THREE.Vector3 | undefined;
  radius: number | undefined;
  view: unknown;
  keepOpen: boolean;
  descriptionVisible: boolean;
  showDescription: boolean;
  actions: Action[];
  isHighlighted: boolean;
  _visible: boolean;
  __visible: boolean;
  _display: boolean;
  _expand: boolean;
  collapseThreshold: number | undefined;
  children: Annotation[];
  parent: Annotation | null;
  boundingBox: THREE.Box3;
  domElement: JQueryLike;
  elTitlebar: JQueryLike;
  elTitle: JQueryLike;
  elDescription: JQueryLike;
  elDescriptionClose: JQueryLike;
  clickTitle: () => void;
  handles: AnnotationHandles | undefined;

  constructor(args: AnnotationArgs = {}) {
    super();

    this.scene = null;
    this._title = args.title || 'No Title';
    this._description = args.description || '';
    this.offset = new THREE.Vector3();
    this.uuid = THREE.MathUtils.generateUUID();

    if (!args.position) {
      this.position = null;
    } else if ('x' in args.position && (args.position as { x: number | null }).x !== null && (args.position as { x: number | null }).x !== undefined) {
      this.position = args.position as THREE.Vector3;
    } else {
      this.position = new THREE.Vector3(...(args.position as number[]));
    }

    this.cameraPosition = args.cameraPosition instanceof Array ? new THREE.Vector3().fromArray(args.cameraPosition) : (args.cameraPosition as THREE.Vector3 | undefined);
    this.cameraTarget = args.cameraTarget instanceof Array ? new THREE.Vector3().fromArray(args.cameraTarget) : (args.cameraTarget as THREE.Vector3 | undefined);
    this.radius = args.radius;
    this.view = args.view || null;
    this.keepOpen = false;
    this.descriptionVisible = false;
    this.showDescription = true;
    this.actions = (args.actions as Action[]) || [];
    this.isHighlighted = false;
    this._visible = true;
    this.__visible = true;
    this._display = true;
    this._expand = false;
    this.collapseThreshold = [args.collapseThreshold, 100].find((e) => e !== undefined);

    this.children = [];
    this.parent = null;
    this.boundingBox = new THREE.Box3();

    const iconClose = resourcePath + '/icons/close.svg';

    this.domElement = $(`
			<div class="annotation" oncontextmenu="return false;">
				<div class="annotation-titlebar">
					<span class="annotation-label"></span>
				</div>
				<div class="annotation-description">
					<span class="annotation-description-close">
						<img src="${iconClose}" width="16px">
					</span>
					<span class="annotation-description-content">${this._description}</span>
				</div>
			</div>
		`);

    this.elTitlebar = this.domElement.find('.annotation-titlebar');
    this.elTitle = this.elTitlebar.find('.annotation-label');
    this.elTitle.append(this._title);
    this.elDescription = this.domElement.find('.annotation-description');
    this.elDescriptionClose = this.elDescription.find('.annotation-description-close');

    this.clickTitle = () => {
      if (this.hasView() && this.scene) {
        this.moveHere(this.scene.getActiveCamera());
      }
      this.dispatchEvent({ type: 'click', target: this });
    };

    this.elTitle.click(this.clickTitle);

    this.actions = this.actions.map((a: Action | Record<string, unknown>) => {
      if (a instanceof Action) {
        return a;
      } else {
        return new Action(a as { icon?: string; tooltip?: string });
      }
    });

    for (const action of this.actions) {
      action.pairWith(this);
    }

    const actions = this.actions.filter((a: Action & { showIn?: string | string[] }) => a.showIn === undefined || (Array.isArray(a.showIn) && a.showIn.includes('scene')));

    for (const action of actions) {
      const elButton = $(`<img src="${action.icon}" class="annotation-action-icon">`);
      this.elTitlebar.append(elButton);
      elButton.click(() => action.onclick({ annotation: this, type: 'click' }));
    }

    this.elDescriptionClose.hover(
      () => this.elDescriptionClose.css('opacity', '1'),
      () => this.elDescriptionClose.css('opacity', '0.5'),
    );
    this.elDescriptionClose.click(() => this.setHighlighted(false));

    this.domElement.mouseenter(() => this.setHighlighted(true));
    this.domElement.mouseleave(() => this.setHighlighted(false));

    this.domElement.on('touchstart', () => {
      this.setHighlighted(!this.isHighlighted);
    });

    this.display = false;
  }

  installHandles(viewer: AnnotationViewer) {
    if (this.handles !== undefined) {
      return;
    }

    const domElement = $(`
			<div style="position: absolute; left: 300; top: 200; pointer-events: none">
				<svg width="300" height="600">
					<line x1="0" y1="0" x2="1200" y2="200" style="stroke: black; stroke-width:2" />
					<circle cx="50" cy="50" r="4" stroke="black" stroke-width="2" fill="gray" />
					<circle cx="150" cy="50" r="4" stroke="black" stroke-width="2" fill="gray" />
				</svg>
			</div>
		`);

    const svg = domElement.find('svg')[0] as unknown as SVGSVGElement;
    const elLine = domElement.find('line')[0] as unknown as SVGLineElement;
    const elStart = domElement.find('circle')[0] as unknown as SVGCircleElement;
    const elEnd = domElement.find('circle')[1] as unknown as SVGCircleElement;

    const setCoordinates = (start: THREE.Vector3, end: THREE.Vector3) => {
      elStart.setAttribute('cx', `${start.x}`);
      elStart.setAttribute('cy', `${start.y}`);

      elEnd.setAttribute('cx', `${end.x}`);
      elEnd.setAttribute('cy', `${end.y}`);

      elLine.setAttribute('x1', `${start.x}`);
      elLine.setAttribute('y1', `${start.y}`);
      elLine.setAttribute('x2', `${end.x}`);
      elLine.setAttribute('y2', `${end.y}`);

      const box = svg.getBBox();
      svg.setAttribute('width', `${box.width}`);
      svg.setAttribute('height', `${box.height}`);
      svg.setAttribute('viewBox', `${box.x} ${box.y} ${box.width} ${box.height}`);

      const ya = start.y - end.y;
      const xa = start.x - end.x;

      if (ya > 0) {
        start.y = start.y - ya;
      }
      if (xa > 0) {
        start.x = start.x - xa;
      }

      domElement.css('left', `${start.x}px`);
      domElement.css('top', `${start.y}px`);
    };

    $(viewer.renderArea).append(domElement as unknown as string);

    let annotationStartPos = this.position?.clone() ?? new THREE.Vector3();
    let annotationStartOffset = this.offset.clone();

    $(this.domElement as unknown as HTMLElement).draggable({
      start: () => {
        annotationStartPos = this.position?.clone() ?? new THREE.Vector3();
        annotationStartOffset = this.offset.clone();
        $(this.domElement as unknown as HTMLElement)
          .find('.annotation-titlebar')
          .css('pointer-events', 'none');
      },
      stop: () => {
        $(this.domElement as unknown as HTMLElement)
          .find('.annotation-titlebar')
          .css('pointer-events', '');
      },
      drag: (_event: unknown, ui: { originalPosition: { left: number; top: number }; position: { left: number; top: number } }) => {
        const renderAreaWidth = viewer.renderer.getSize(new THREE.Vector2()).width;

        const diff = {
          x: ui.originalPosition.left - ui.position.left,
          y: ui.originalPosition.top - ui.position.top,
        };

        const nDiff = {
          x: -(diff.x / renderAreaWidth) * 2,
          y: (diff.y / renderAreaWidth) * 2,
        };

        const camera = viewer.scene.getActiveCamera();
        const oldScreenPos = new THREE.Vector3().addVectors(annotationStartPos, annotationStartOffset).project(camera);

        const newScreenPos = oldScreenPos.clone();
        newScreenPos.x += nDiff.x;
        newScreenPos.y += nDiff.y;

        const newPos = newScreenPos.clone();
        newPos.unproject(camera);

        const currentPos = this.position ?? new THREE.Vector3();
        const newOffset = new THREE.Vector3().subVectors(newPos, currentPos);
        this.offset.copy(newOffset);
      },
    });

    const updateCallback = () => {
      const renderAreaSize = viewer.renderer.getSize(new THREE.Vector2());
      const renderAreaWidth = renderAreaSize.width;
      const renderAreaHeight = renderAreaSize.height;

      const positionOrOrigin = this.position ?? new THREE.Vector3();
      let start = positionOrOrigin.clone();
      let end = new THREE.Vector3().addVectors(positionOrOrigin, this.offset);

      const toScreen = (pos: THREE.Vector3) => {
        const camera = viewer.scene.getActiveCamera();
        const screenPos = new THREE.Vector3();

        const worldView = new THREE.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
        const ndc = new THREE.Vector4(pos.x, pos.y, pos.z, 1.0).applyMatrix4(worldView);
        // limit w to small positive value, in case position is behind the camera
        ndc.w = Math.max(ndc.w, 0.1);
        ndc.divideScalar(ndc.w);

        screenPos.copy(ndc as unknown as THREE.Vector3);
        screenPos.x = (renderAreaWidth * (screenPos.x + 1)) / 2;
        screenPos.y = renderAreaHeight * (1 - (screenPos.y + 1) / 2);

        return screenPos;
      };

      start = toScreen(start);
      end = toScreen(end);

      setCoordinates(start, end);
    };

    viewer.addEventListener('update', updateCallback);

    this.handles = {
      domElement: domElement,
      setCoordinates: setCoordinates,
      updateCallback: updateCallback,
    };
  }

  removeHandles(viewer: AnnotationViewer) {
    if (this.handles === undefined) {
      return;
    }

    this.handles.domElement.remove();
    viewer.removeEventListener('update', this.handles.updateCallback);

    delete this.handles;
  }

  get visible() {
    return this._visible;
  }

  set visible(value: boolean) {
    if (this._visible === value) {
      return;
    }

    this._visible = value;

    this.dispatchEvent({
      type: 'visibility_changed',
      annotation: this,
    });
  }

  get display() {
    return this._display;
  }

  set display(display: boolean) {
    if (this._display === display) {
      return;
    }

    this._display = display;

    if (display) {
      this.domElement.show();
    } else {
      this.domElement.hide();
    }
  }

  get expand() {
    return this._expand;
  }

  set expand(expand: boolean) {
    if (this._expand === expand) {
      return;
    }

    if (expand) {
      this.display = false;
    } else {
      this.display = true;
      this.traverseDescendants((node: Annotation) => {
        node.display = false;
      });
    }

    this._expand = expand;
  }

  get title() {
    return this._title;
  }

  set title(title: string) {
    if (this._title === title) {
      return;
    }

    this._title = title;
    this.elTitle.empty();
    this.elTitle.append(this._title);

    this.dispatchEvent({
      type: 'annotation_changed',
      annotation: this,
    });
  }

  get description() {
    return this._description;
  }

  set description(description: string) {
    if (this._description === description) {
      return;
    }

    this._description = description;

    const elDescriptionContent = this.elDescription.find('.annotation-description-content');
    elDescriptionContent.empty();
    elDescriptionContent.append(this._description);

    this.dispatchEvent({
      type: 'annotation_changed',
      annotation: this,
    });
  }

  add(annotation: Annotation) {
    if (!this.children.includes(annotation)) {
      this.children.push(annotation);
      annotation.parent = this;

      const descendants: Annotation[] = [];
      annotation.traverse((a: Annotation) => {
        descendants.push(a);
      });

      for (const descendant of descendants) {
        this.dispatchEvent({
          type: 'annotation_added',
          annotation: descendant,
        });
        let ancestor: Annotation | null = this.parent;
        while (ancestor !== null) {
          ancestor.dispatchEvent({
            type: 'annotation_added',
            annotation: descendant,
          });
          ancestor = ancestor.parent;
        }
      }
    }
  }

  level(): number {
    if (this.parent === null) {
      return 0;
    } else {
      return this.parent.level() + 1;
    }
  }

  hasChild(annotation: Annotation) {
    return this.children.includes(annotation);
  }

  remove(annotation: Annotation) {
    if (this.hasChild(annotation)) {
      annotation.removeAllChildren();
      annotation.dispose();
      this.children = this.children.filter((e) => e !== annotation);
      annotation.parent = null;
    }
  }

  removeAllChildren() {
    this.children.forEach((child) => {
      if (child.children.length > 0) {
        child.removeAllChildren();
      }

      this.remove(child);
    });
  }

  updateBounds() {
    const box = new THREE.Box3();

    if (this.position) {
      box.expandByPoint(this.position);
    }

    for (const child of this.children) {
      child.updateBounds();

      box.union(child.boundingBox);
    }

    this.boundingBox.copy(box);
  }

  traverse(handler: (annotation: Annotation) => boolean | undefined | void) {
    const expand = handler(this);

    if (expand === undefined || expand === true) {
      for (const child of this.children) {
        child.traverse(handler);
      }
    }
  }

  traverseDescendants(handler: (annotation: Annotation) => boolean | undefined | void) {
    for (const child of this.children) {
      child.traverse(handler);
    }
  }

  flatten(): Annotation[] {
    const annotations: Annotation[] = [];

    this.traverse((annotation: Annotation) => {
      annotations.push(annotation);
    });

    return annotations;
  }

  descendants(): Annotation[] {
    const annotations: Annotation[] = [];

    this.traverse((annotation: Annotation) => {
      if (annotation !== this) {
        annotations.push(annotation);
      }
    });

    return annotations;
  }

  setHighlighted(highlighted: boolean) {
    if (highlighted) {
      this.domElement.css('opacity', '0.8');
      this.elTitlebar.css('box-shadow', '0 0 5px #fff');
      this.domElement.css('z-index', '1000');

      if (this._description) {
        this.descriptionVisible = true;
        this.elDescription.fadeIn(200);
        this.elDescription.css('position', 'relative');
      }
    } else {
      this.domElement.css('opacity', '0.5');
      this.elTitlebar.css('box-shadow', '');
      this.domElement.css('z-index', '100');
      this.descriptionVisible = false;
      this.elDescription.css('display', 'none');
    }

    this.isHighlighted = highlighted;
  }

  hasView() {
    let hasPosTargetView = this.cameraTarget !== undefined && this.cameraTarget.x !== null && this.cameraTarget.x !== undefined;
    hasPosTargetView = hasPosTargetView && this.cameraPosition !== undefined && this.cameraPosition.x !== null && this.cameraPosition.x !== undefined;

    const hasRadiusView = this.radius !== undefined;

    const hasView = hasPosTargetView || hasRadiusView;

    return hasView;
  }

  moveHere(_camera: THREE.Camera) {
    if (!this.hasView()) {
      return;
    }

    const scene = this.scene;
    if (!scene) return;

    const view = scene.view;
    const easing = TWEEN.Easing.Quartic.Out;

    let endTarget: THREE.Vector3;
    if (this.cameraTarget) {
      endTarget = this.cameraTarget;
    } else if (this.position) {
      endTarget = this.position;
    } else {
      endTarget = this.boundingBox.getCenter(new THREE.Vector3());
    }

    if (this.cameraPosition) {
      const endPosition = this.cameraPosition;

      Utils.moveTo(scene, endPosition, endTarget);
    } else if (this.radius) {
      const direction = view.direction;
      const endPosition = endTarget.clone().add(direction.multiplyScalar(-this.radius));
      const startRadius = view.radius;
      const endRadius = this.radius;

      {
        // animate camera position
        const tween = new TWEEN.Tween(view.position).to(endPosition, 500);
        tween.easing(easing);
        tween.start();
      }

      {
        // animate radius
        const t = { x: 0 };

        const tween = new TWEEN.Tween(t).to({ x: 1 }, 500).onUpdate(function (this: { x: number }) {
          view.radius = this.x * endRadius + (1 - this.x) * startRadius;
        });
        tween.easing(easing);
        tween.start();
      }
    }
  }

  dispose() {
    if (this.domElement.parentElement) {
      this.domElement.parentElement.removeChild(this.domElement as unknown as Node);
    }
  }

  toString() {
    return 'Annotation: ' + this._title;
  }
}
